import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning, WhatsappLogo, Copy, PaperPlaneTilt, EnvelopeSimple, Wrench } from "@phosphor-icons/react";
import { AdminUsersPanel } from "./AdminUsersPanel";

const empty = { account_sid: "", auth_token: "", from_number: "", template_sid: "" };

const INTAKE_SAMPLE = `Good day ResQconnect team,

We require urgent armed response dispatch.

Person on site: Pieter Botha
Contact: 082 555 1234
Location: 14 Witkoppen Rd, Paulshof, Sandton
GPS: -26.0341, 28.0289

Situation: Battery disconnect alarm triggered on tower AB-114, suspected tampering in progress. Site technician is on route and requires escort.

Regards,
AB Skeye Tech Control Room`;

export default function SettingsPage() {
  const [form, setForm] = useState(empty);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [testing, setTesting] = useState(false);
  // Diagnostics — inbound intake simulator
  const [intakeFrom, setIntakeFrom] = useState("ops@abskeye.co.za");
  const [intakeSubject, setIntakeSubject] = useState("URGENT: Dispatch required - Tower AB-114 Paulshof");
  const [intakeBody, setIntakeBody] = useState(INTAKE_SAMPLE);
  const [intakeBusy, setIntakeBusy] = useState(false);
  // CSG dispatch mode (live vs rehearsal) + Resend inbound webhook URL
  const [csg, setCsg] = useState(null);
  const [csgBusy, setCsgBusy] = useState(false);
  const loadCsg = () => api.get("/settings/csg-mode").then((r) => setCsg(r.data)).catch(() => {});
  const [inbound, setInbound] = useState(null);
  const loadInbound = () => api.get("/diagnostics/inbound-events").then((r) => setInbound(r.data)).catch(() => {});
  useEffect(() => { loadCsg(); loadInbound(); loadMail(); loadTrack(); }, []);
  const setCsgMode = async (live) => {
    if (live && !window.confirm("Switch to LIVE dispatch? Every dispatch will reach the real CSG command centre.")) return;
    setCsgBusy(true);
    try {
      await api.post("/settings/csg-mode", { live });
      toast.success(live ? "LIVE dispatch — real teams will be sent" : "Simulation mode — nothing reaches CSG");
      loadCsg();
    } catch (e) { toast.error("Couldn't change mode", { description: e.response?.data?.detail || e.message }); }
    finally { setCsgBusy(false); }
  };

  const [mail, setMail] = useState(null);
  const [mailBusy, setMailBusy] = useState(false);
  const [track, setTrack] = useState(null);
  const [trackBusy, setTrackBusy] = useState(false);
  const loadTrack = () => api.get("/settings/email-tracking").then((r) => setTrack(r.data)).catch(() => {});
  const setTracking = async (d, open_tracking, click_tracking) => {
    setTrackBusy(true);
    try {
      const { data } = await api.post("/settings/email-tracking", { domain_id: d.id, open_tracking, click_tracking });
      setTrack(data);
      toast.success(open_tracking || click_tracking ? "Tracking switched on for " + d.name : "Tracking switched off for " + d.name,
        { description: "Add the CNAME record shown below at your DNS provider, then hit Verify." });
    } catch (e) { toast.error("Resend wouldn't change it", { description: e.response?.data?.detail || e.message }); }
    finally { setTrackBusy(false); }
  };
  const verifyTracking = async (d) => {
    setTrackBusy(true);
    try {
      const { data } = await api.post("/settings/email-tracking/verify", { domain_id: d.id });
      setTrack(data);
      const now = data.domains.find((x) => x.id === d.id);
      if (now?.tracking_active) toast.success("Tracking is live on " + d.name);
      else toast.message("Still waiting on DNS", { description: "The CNAME isn't visible yet — DNS can take a few minutes." });
    } catch (e) { toast.error("Verify failed", { description: e.response?.data?.detail || e.message }); }
    finally { setTrackBusy(false); }
  };
  const loadMail = () => api.get("/settings/email-mode").then((r) => setMail(r.data)).catch(() => {});
  const setMailMode = async (live) => {
    if (live && !window.confirm("Switch email to LIVE? Every member email — migration, farewell, welcome — will go to real subscribers.")) return;
    setMailBusy(true);
    try {
      await api.post("/settings/email-mode", { live });
      toast.success(live ? "LIVE email — real subscribers will receive mail" : "Allowlist only — nobody else receives mail");
      loadMail();
    } catch (e) { toast.error("Couldn't change email mode", { description: e.response?.data?.detail || e.message }); }
    finally { setMailBusy(false); }
  };

  const load = () => api.get("/settings/twilio").then((r) => {
    setInfo(r.data);
    setForm({
      account_sid: r.data.account_sid || "",
      auth_token: "",
      from_number: r.data.from_number || "",
      template_sid: r.data.template_sid || "",
    });
  });

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/settings/twilio", form);
      toast.success(r.data.configured ? "Twilio saved — WhatsApp alerts are LIVE" : "Twilio saved — still missing required fields");
      load();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testNumber.trim()) { toast.error("Enter a WhatsApp number first"); return; }
    setTesting(true);
    try {
      const r = await api.post("/settings/twilio/test", { to: testNumber });
      if (String(r.data.status).startsWith("sent")) toast.success(`Test sent to ${r.data.to}`, { description: r.data.status });
      else toast.error("Test failed", { description: r.data.status });
    } catch (e) {
      toast.error("Test failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const simulateInbound = async () => {
    if (!intakeFrom || !intakeBody) return toast.error("Sender and body are required");
    setIntakeBusy(true);
    try {
      const r = await api.post("/inbound/email", { from_email: intakeFrom, subject: intakeSubject, body: intakeBody });
      if (r.data?.dropped) {
        toast.error(`Dropped — ${r.data.reason}`, { description: `Sender ${r.data.sender} is not on the allowlist.` });
      } else if (r.data?.ref) {
        toast.success(`Incident Received — ${r.data.ref}`, {
          description: r.data.client_name ? `Identified: ${r.data.client_name}` : "Sender not matched to a client entry.",
        });
      } else {
        toast.success("Processed", { description: JSON.stringify(r.data).slice(0, 120) });
      }
    } catch (e) {
      toast.error("Intake failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setIntakeBusy(false);
    }
  };

  if (!info) return <div className="p-8 text-zinc-500 font-mono-data">LOADING SETTINGS...</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Settings</h1>

      <AdminUsersPanel />

      {mail && (
        <div className={`border p-5 mt-6 ${mail.live ? "border-green-500/40 bg-green-500/10" : "border-yellow-500/40 bg-yellow-500/10"}`} data-testid="email-mode-panel">
          <div className="flex items-start gap-3">
            {mail.live ? <PaperPlaneTilt size={22} weight="duotone" className="text-green-400 shrink-0 mt-0.5" /> : <Warning size={22} weight="duotone" className="text-yellow-400 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div className={`font-bold ${mail.live ? "text-green-400" : "text-yellow-400"}`} data-testid="email-mode-label">
                {mail.live ? "LIVE EMAIL — real subscribers receive mail" : "EMAIL HELD — only the test allowlist receives mail"}
              </div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                {mail.live
                  ? "Migration, farewell and welcome emails go to whoever is on the list. Switch this off before rehearsing a broadcast."
                  : `Everything to anyone other than ${mail.allowlist.join(", ")} is blocked and reported as "blocked". Turn this on when you are ready to mail real members.`}
                <span className="block mt-1 text-zinc-500" data-testid="email-mode-source">
                  {mail.set_by_admin ? "Set here in the console" : "Following the PROD_EMAIL_ENABLED setting"} · env value {JSON.stringify(mail.env_value)} reads as {String(mail.env_flag)} · Resend key {mail.api_key_configured ? "configured" : "MISSING"}
                  {mail.changed_at ? ` · last changed ${String(mail.changed_at).slice(0, 16).replace("T", " ")}${mail.changed_by ? ` by ${mail.changed_by}` : ""}` : ""}
                </span>
              </div>
            </div>
            <button onClick={() => setMailMode(!mail.live)} disabled={mailBusy} data-testid="email-mode-toggle"
              className={`btn-tactical shrink-0 text-white ${mail.live ? "bg-yellow-600 hover:bg-yellow-500" : "bg-green-600 hover:bg-green-500"} disabled:opacity-40`}>
              {mailBusy ? "…" : mail.live ? "Hold email" : "Go live with email"}
            </button>
          </div>
        </div>
      )}

      {csg && (
        <div className={`border p-5 mt-6 ${csg.live ? "border-red-500/40 bg-red-500/10" : "border-yellow-500/40 bg-yellow-500/10"}`} data-testid="csg-mode-panel">
          <div className="flex items-start gap-3">
            {csg.live ? <Warning size={22} weight="duotone" className="text-red-400 shrink-0 mt-0.5" /> : <Wrench size={22} weight="duotone" className="text-yellow-400 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div className={`font-bold ${csg.live ? "text-red-400" : "text-yellow-400"}`} data-testid="csg-mode-label">
                {csg.live ? "LIVE DISPATCH — reaching the real CSG command centre" : "SIMULATION MODE — nothing reaches CSG"}
              </div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                {csg.live
                  ? "Every dispatch creates a real occurrence and sends real tactical teams. Switch to simulation before rehearsing migration, demos or tests."
                  : "Dispatches are logged and marked SIMULATED with a synthetic SIM- reference. Real panics will NOT reach responders — switch back to live before go-live."}
                {csg.changed_at ? <span className="block mt-1 text-zinc-500">Last changed {String(csg.changed_at).slice(0, 16).replace("T", " ")}{csg.changed_by ? ` by ${csg.changed_by}` : ""}</span> : null}
              </div>
            </div>
            <button onClick={() => setCsgMode(!csg.live)} disabled={csgBusy} data-testid="csg-mode-toggle"
              className={`btn-tactical shrink-0 text-white ${csg.live ? "bg-yellow-600 hover:bg-yellow-500" : "bg-red-600 hover:bg-red-500"} disabled:opacity-40`}>
              {csgBusy ? "…" : csg.live ? "Switch to simulation" : "Go live"}
            </button>
          </div>
          {csg.webhook_url && (
            <div className="mt-4 pt-4 border-t border-zinc-700/50">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Resend inbound webhook URL</div>
              <div className="flex items-center gap-3 font-mono-data text-xs">
                <span className="flex-1 truncate text-green-400" data-testid="resend-webhook-url">{csg.webhook_url}</span>
                <button onClick={() => copy(csg.webhook_url)} className="text-zinc-400 hover:text-white" data-testid="resend-copy-webhook"><Copy size={16} /></button>
              </div>
              {inbound && (
                <div className="mt-4" data-testid="inbound-feed">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Live inbound feed · {inbound.total} event{inbound.total === 1 ? "" : "s"}
                    </div>
                    <button onClick={loadInbound} className="text-xs text-zinc-400 hover:text-white underline" data-testid="inbound-refresh">Refresh</button>
                  </div>
                  {inbound.events.length === 0 ? (
                    <div className="text-xs text-zinc-400 leading-relaxed" data-testid="inbound-empty">
                      Nothing received yet. Send an email to your dispatch inbox — every delivery Resend makes
                      will appear here within seconds, so you can prove intake works end to end.
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-56 overflow-y-auto">
                      {inbound.events.map((e, i) => (
                        <div key={e.id} className="flex items-center gap-3 text-xs font-mono-data py-1 border-b border-zinc-800/60 last:border-0" data-testid={`inbound-event-${i}`}>
                          <span className="text-zinc-500 shrink-0">{String(e.at).slice(5, 16).replace("T", " ")}</span>
                          <span className={`shrink-0 ${e.outcome === "incident_created" ? "text-green-400" : e.outcome === "bad_signature" || e.outcome === "bounced" ? "text-red-400" : "text-zinc-400"}`}>
                            {e.outcome === "incident_created" ? `✓ ${e.ref}` : e.outcome}
                          </span>
                          <span className="text-zinc-400 truncate">{e.sender || (e.recipients || []).join(", ")}{e.subject ? ` · ${e.subject}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!inbound.secret_configured && (
                    <div className="text-xs text-red-400 mt-2" data-testid="inbound-no-secret">
                      RESEND_WEBHOOK_SECRET is not set — signatures are not being checked.
                    </div>
                  )}
                </div>
              )}
              <div className="text-xs text-zinc-400 mt-3 leading-relaxed">
                Paste the URL above into the Resend dashboard → Webhooks, then re-enable delivery. Subscribe to
                <span className="font-mono-data"> email.received</span> for dispatch intake, and to
                <span className="font-mono-data"> email.sent / email.delivered / email.bounced</span> to track whether
                members actually receive their mail.
              </div>
            </div>
          )}
        </div>
      )}
      {track && (
        <div className="border border-zinc-800 bg-[#121214] p-5 mt-6" data-testid="email-tracking-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Open &amp; click tracking</div>
              <div className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">
                Switch this on and Resend reports who opened each member email and who clicked the link.
                Mail History then shows a "read" badge per person. Click tracking rewrites links through
                Resend's tracking subdomain.
              </div>
            </div>
            <button onClick={loadTrack} className="text-xs text-zinc-400 hover:text-white underline" data-testid="email-tracking-refresh">Refresh</button>
          </div>

          {!track.api_key_configured && (
            <div className="text-xs text-red-400 mt-3" data-testid="email-tracking-no-key">No RESEND_API_KEY in this environment.</div>
          )}
          {track.error && (
            <div className="text-xs text-red-400 mt-3" data-testid="email-tracking-error">{track.error}</div>
          )}

          {track.domains.map((d) => (
            <div key={d.id} className="mt-4 pt-4 border-t border-zinc-800/60" data-testid={`email-tracking-domain-${d.name}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-mono-data text-sm text-white">{d.name}
                    <span className={`ml-2 text-[10px] uppercase tracking-wider ${d.tracking_active ? "text-green-400" : "text-yellow-400"}`}>
                      {d.tracking_active ? "tracking live" : d.open_tracking || d.click_tracking ? "waiting on DNS" : "not tracking"}
                    </span>
                  </div>
                  <div className="text-xs mt-1" data-testid={`email-tracking-state-${d.name}`}>
                    <span className={d.open_tracking ? "text-green-400" : "text-zinc-500"}>Open tracking {d.open_tracking ? "ON" : "off"}</span>
                    <span className="text-zinc-600"> · </span>
                    <span className={d.click_tracking ? "text-green-400" : "text-zinc-500"}>Click tracking {d.click_tracking ? "ON" : "off"}</span>
                    {d.tracking_subdomain ? <span className="text-zinc-500"> · links via {d.tracking_subdomain}.{d.name}</span> : null}
                  </div>
                </div>
                <div className="flex gap-2">
                  {(!d.open_tracking || !d.click_tracking) && (
                    <button onClick={() => setTracking(d, true, true)} disabled={trackBusy} data-testid={`email-tracking-on-${d.name}`}
                      className="btn-tactical bg-green-600 hover:bg-green-500 text-white disabled:opacity-40">
                      {trackBusy ? "…" : "Switch tracking on"}
                    </button>
                  )}
                  {(d.open_tracking || d.click_tracking) && (
                    <>
                      {!d.tracking_active && (
                        <button onClick={() => verifyTracking(d)} disabled={trackBusy} data-testid={`email-tracking-verify-${d.name}`}
                          className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white disabled:opacity-40">
                          {trackBusy ? "…" : "I've added the record — verify"}
                        </button>
                      )}
                      <button onClick={() => setTracking(d, false, false)} disabled={trackBusy} data-testid={`email-tracking-off-${d.name}`}
                        className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white disabled:opacity-40">
                        Switch off
                      </button>
                    </>
                  )}
                </div>
              </div>
              {d.tracking_record && d.tracking_record.status !== "verified" && (
                <div className="mt-3 border border-yellow-500/30 bg-yellow-500/5 p-3" data-testid={`email-tracking-dns-${d.name}`}>
                  <div className="text-xs text-yellow-400 font-bold">One DNS record to add at Cloudflare, then press verify</div>
                  <div className="font-mono-data text-[11px] text-zinc-300 mt-2 leading-relaxed">
                    <div>Type: <span className="text-white">{d.tracking_record.type}</span></div>
                    <div className="flex items-center gap-2">
                      <span>Name: <span className="text-white">{d.tracking_record.name}</span></span>
                      <button onClick={() => copy(d.tracking_record.name)} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Value: <span className="text-white">{d.tracking_record.value}</span></span>
                      <button onClick={() => copy(d.tracking_record.value)} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
                    </div>
                    <div>Proxy: <span className="text-white">DNS only (grey cloud)</span> · TTL Auto · status {d.tracking_record.status}</div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="mt-4 pt-4 border-t border-zinc-800/60">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Webhook events we have received</div>
            <div className="grid sm:grid-cols-2 gap-1" data-testid="email-tracking-events">
              {Object.entries(track.event_counts).map(([ev, n]) => (
                <div key={ev} className="flex items-center gap-2 text-xs font-mono-data" data-testid={`email-event-${ev}`}>
                  {n > 0
                    ? <CheckCircle size={14} weight="fill" className="text-green-400 shrink-0" />
                    : <Warning size={14} weight="fill" className="text-yellow-400 shrink-0" />}
                  <span className={n > 0 ? "text-zinc-300" : "text-yellow-400"}>{ev}</span>
                  <span className="text-zinc-500">{n > 0 ? `${n} received` : "never arrived"}</span>
                </div>
              ))}
            </div>
            {track.missing_events.length > 0 && (
              <div className="text-xs text-yellow-400 mt-3 leading-relaxed" data-testid="email-tracking-missing">
                Tick {track.missing_events.join(", ")} on the webhook in the Resend dashboard
                ({track.webhook_url}) — until then those events never reach us and Mail History cannot
                show opens or clicks.
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-zinc-400 text-sm mt-6">Configure the ResQconnect-branded Twilio WhatsApp sender. Once these four values are in, CallMeBot goes offline and every dispatch is pushed automatically — no shared bot, no filling up.</p>

      <div className={`border p-4 mt-6 flex items-center gap-3 ${info.configured ? "border-green-500/30 bg-green-500/10" : "border-yellow-500/30 bg-yellow-500/10"}`} data-testid="twilio-status">
        {info.configured
          ? <><CheckCircle size={22} weight="duotone" className="text-green-400" /><div><div className="font-bold text-green-400">Twilio WhatsApp is LIVE</div><div className="text-xs text-zinc-400">Alerts are pushed via {info.from_number}</div></div></>
          : <><Warning size={22} weight="duotone" className="text-yellow-400" /><div><div className="font-bold text-yellow-400">Twilio not fully configured</div><div className="text-xs text-zinc-400">WhatsApp alerts are paused. Email dispatch still works.</div></div></>
        }
      </div>

      <div className="border border-zinc-800 bg-[#121214] p-6 mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Account SID <span className="text-zinc-600 normal-case tracking-normal">(starts AC...)</span></label>
          <input className="tactical-input mt-1 font-mono-data text-xs" value={form.account_sid}
            onChange={(e) => setForm({ ...form, account_sid: e.target.value })}
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-sid-input" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Auth Token {info.auth_token_set && <span className="text-green-400 normal-case tracking-normal">(saved — leave blank to keep)</span>}</label>
          <input type="password" className="tactical-input mt-1 font-mono-data text-xs" value={form.auth_token}
            onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
            placeholder={info.auth_token_set ? "••••••••••••••••••" : "your_auth_token"} data-testid="twilio-token-input" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">WhatsApp Sender <span className="text-zinc-600 normal-case tracking-normal">(e.g. whatsapp:+27821234567)</span></label>
          <input className="tactical-input mt-1 font-mono-data text-xs" value={form.from_number}
            onChange={(e) => setForm({ ...form, from_number: e.target.value })}
            placeholder="whatsapp:+27821234567" data-testid="twilio-from-input" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Dispatch Template SID <span className="text-zinc-600 normal-case tracking-normal">(HX... — optional but needed for out-of-session)</span></label>
          <input className="tactical-input mt-1 font-mono-data text-xs" value={form.template_sid}
            onChange={(e) => setForm({ ...form, template_sid: e.target.value })}
            placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-template-input" />
        </div>
        <div className="md:col-span-2">
          <button onClick={save} disabled={saving} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white" data-testid="twilio-save-button">
            {saving ? "SAVING..." : "SAVE TWILIO CREDENTIALS"}
          </button>
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#121214] p-6 mt-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Inbound webhook — paste into Twilio Console</div>
        <div className="flex items-center gap-2 font-mono-data text-xs bg-black/40 p-3 border border-zinc-800">
          <span className="flex-1 truncate text-green-400" data-testid="twilio-webhook-url">{info.webhook_url}</span>
          <button onClick={() => copy(info.webhook_url)} className="text-zinc-400 hover:text-white" data-testid="twilio-copy-webhook"><Copy size={16} /></button>
        </div>
        <p className="text-zinc-500 text-xs mt-2">Twilio Console → Messaging → Senders → your WhatsApp Sender → &ldquo;When a message comes in&rdquo; → HTTP POST → paste the URL above.</p>
      </div>

      <div className="border border-zinc-800 bg-[#121214] p-6 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <WhatsappLogo size={20} weight="duotone" className="text-green-400" />
          <span className="font-heading font-bold uppercase text-sm tracking-wider">Send a live test</span>
        </div>
        <div className="flex gap-2">
          <input className="tactical-input font-mono-data text-xs flex-1" value={testNumber}
            onChange={(e) => setTestNumber(e.target.value)}
            placeholder="+27821234567 (must have replied to your Twilio sender at least once)" data-testid="twilio-test-number" />
          <button onClick={sendTest} disabled={testing || !info.configured} className="btn-tactical bg-green-600 hover:bg-green-500 text-white flex items-center gap-2 disabled:opacity-40" data-testid="twilio-test-button">
            <PaperPlaneTilt size={14} /> {testing ? "SENDING..." : "SEND TEST"}
          </button>
        </div>
        {!info.configured && <p className="text-yellow-400 text-xs mt-2">Save valid credentials first.</p>}
      </div>

      <details className="mt-6 border border-zinc-800 bg-[#121214]">
        <summary className="px-6 py-3 cursor-pointer font-heading uppercase tracking-wider text-sm">Twilio setup checklist</summary>
        <ol className="p-6 pt-0 space-y-2 text-sm text-zinc-300 list-decimal ml-4">
          <li>Sign up at twilio.com and buy a WhatsApp-enabled number (or use the free Sandbox for testing).</li>
          <li>Messaging → Senders → WhatsApp Senders → register your number, display name <b>ResQconnect Dispatch</b>, category <b>Public Safety / Emergency</b>.</li>
          <li>Content Template Builder → new template named <code>resq_dispatch_alert</code>, category <b>Utility</b>. Body:
            <pre className="bg-black/40 p-2 mt-1 text-xs whitespace-pre-wrap">🚨 ResQconnect Dispatch 🚨{"\n"}Ref: {"{{1}}"}{"\n"}Client: {"{{2}}"}{"\n"}Location: {"{{3}}"}{"\n"}Details: {"{{4}}"}{"\n"}Respond ACK to acknowledge.</pre>
          </li>
          <li>Copy Account SID, Auth Token, WhatsApp Sender (as <code>whatsapp:+27...</code>), and Template Content SID above and save.</li>
          <li>Paste the inbound webhook URL from this page into Twilio Console (see box above).</li>
          <li>Go to Crew → hit the yellow paper-plane button next to each responder — they get an email, tap once, done.</li>
        </ol>
      </details>

      {/* ─────────────── DIAGNOSTICS ─────────────── */}
      <div className="mt-14 flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-[0.18em] text-[#5B6B7E]">
        <span className="w-6 h-px bg-[#E01A2E]" /> DIAGNOSTICS
      </div>
      <h2
        className="mt-2 text-3xl leading-tight tracking-tight text-[#1A1E24]"
        style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500, letterSpacing: "-0.022em" }}
      >
        Inbound <em style={{ fontStyle: "italic", color: "#5B6B7E", fontWeight: 400 }}>pipeline</em> reference &amp; simulator.
      </h2>
      <p className="text-[#5B6B7E] text-sm mt-2 max-w-2xl">
        Real dispatch emails flow in automatically from <span className="font-mono-data text-[#1A1E24]">dispatch@resqconnect.co.za</span>. This section is for onboarding a new client, sanity-checking a webhook, or reproducing a parse bug — no operator action needed day to day.
      </p>

      <details className="mt-4 border border-zinc-800 bg-[#121214]" data-testid="diagnostics-webhook">
        <summary className="px-6 py-3 cursor-pointer flex items-center gap-2 text-sm">
          <EnvelopeSimple size={16} weight="duotone" />
          <span className="font-heading uppercase tracking-wider">Inbound webhook endpoints</span>
        </summary>
        <div className="p-6 pt-0">
          <div className="font-mono-data text-xs text-zinc-300 bg-[#09090b] border border-zinc-800 p-4 overflow-x-auto" data-testid="webhook-url">
            <span className="text-green-400"># Resend inbound (recommended — set as webhook for the email.received event)</span>
            <br />POST {BACKEND_URL}/api/inbound/resend
            <br /><br />
            <span className="text-green-400"># Generic inbound (SendGrid / Mailgun / custom)</span>
            <br />POST {BACKEND_URL}/api/inbound/email
            <br /><span className="text-zinc-500">{`{ "from_email": "...", "subject": "...", "body": "..." }`}</span>
          </div>
          <p className="text-zinc-500 text-xs mt-3">
            Resend setup: Dashboard → Domains → add the MX record for resqconnect.co.za, then Webhooks → Add Webhook → select &ldquo;email.received&rdquo; → paste the <code>/api/inbound/resend</code> URL.
          </p>
        </div>
      </details>

      <details className="mt-4 border border-zinc-800 bg-[#121214]" data-testid="diagnostics-simulate">
        <summary className="px-6 py-3 cursor-pointer flex items-center gap-2 text-sm">
          <Wrench size={16} weight="duotone" />
          <span className="font-heading uppercase tracking-wider">Simulate inbound email</span>
        </summary>
        <div className="p-6 pt-0 grid gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">From (Sender Email)</label>
            <input className="tactical-input mt-1" value={intakeFrom} onChange={(e) => setIntakeFrom(e.target.value)} data-testid="intake-from-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Subject</label>
            <input className="tactical-input mt-1" value={intakeSubject} onChange={(e) => setIntakeSubject(e.target.value)} data-testid="intake-subject-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Email Body</label>
            <textarea className="tactical-input mt-1 min-h-[200px] font-mono-data text-xs" value={intakeBody} onChange={(e) => setIntakeBody(e.target.value)} data-testid="intake-body-input" />
          </div>
          <button
            onClick={simulateInbound}
            disabled={intakeBusy}
            className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white flex items-center gap-2 w-fit"
            data-testid="intake-submit-button"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {intakeBusy ? "Processing with AI..." : "Process inbound email"}
          </button>
          <p className="text-zinc-500 text-xs">
            Simulated events flow through the exact same pipeline as real inbound emails. Unrecognised senders are dropped by the allowlist. Complete incidents auto-dispatch to armed response.
          </p>
        </div>
      </details>
    </div>
  );
}
